/**
 * Paper layout — mirror of `backend/src/utils/paperLayout.ts`. Keep the two
 * files 1:1 (types + algorithm). See the backend file for full documentation.
 */

export interface LayoutQuestion {
  _id: string;
  order: number;
  marks: number;
  choiceGroupId?: string | null;
  choiceGroupLabel?: string | null;
  subGroupId?: string | null;
  subGroupRole?: 'parent' | 'child' | null;
  [k: string]: any;
}

export interface LayoutSectionInput {
  section: { _id: string; [k: string]: any };
  questions: LayoutQuestion[];
  /** When set, question numbering starts from this value + 1 instead of
   *  restarting at 1. Owned by the caller across sections. */
  startCounter?: number;
}

export interface LayoutSectionResult {
  items: RenderItem[];
  endCounter: number;
}

export interface RenderQuestionItem {
  kind: 'question';
  question: LayoutQuestion;
  questionNumber: number;
}

export interface RenderChoiceGroupItem {
  kind: 'choice-group';
  choiceGroupId: string;
  label: string;
  members: RenderQuestionItem[];
}

export interface RenderParentGroupItem {
  kind: 'parent-group';
  subGroupId: string;
  parent: RenderQuestionItem;
  children: Array<{
    question: LayoutQuestion;
    subPartIndex: number;
  }>;
}

export type RenderItem = RenderQuestionItem | RenderChoiceGroupItem | RenderParentGroupItem;

const byOrder = <T extends { order: number }>(a: T, b: T) => a.order - b.order;

const groupChoicesInRun = (
  questions: LayoutQuestion[],
  assignNumber: () => number,
): Array<RenderQuestionItem | RenderChoiceGroupItem> => {
  const items: Array<RenderQuestionItem | RenderChoiceGroupItem> = [];
  let i = 0;
  while (i < questions.length) {
    const q = questions[i];
    const gid = q.choiceGroupId || null;

    if (!gid) {
      items.push({ kind: 'question', question: q, questionNumber: assignNumber() });
      i += 1;
      continue;
    }

    const memberQs: LayoutQuestion[] = [];
    let j = i;
    while (j < questions.length && questions[j].choiceGroupId === gid) {
      memberQs.push(questions[j]);
      j += 1;
    }

    if (memberQs.length === 1) {
      items.push({ kind: 'question', question: memberQs[0], questionNumber: assignNumber() });
    } else {
      const groupNumber = assignNumber();
      const members: RenderQuestionItem[] = memberQs.map(q => ({
        kind: 'question', question: q, questionNumber: groupNumber,
      }));
      const label = (memberQs[0].choiceGroupLabel || 'OR').trim() || 'OR';
      items.push({ kind: 'choice-group', choiceGroupId: gid, label, members });
    }
    i = j;
  }
  return items;
};

export const buildSectionRenderItems = (
  input: LayoutSectionInput,
): LayoutSectionResult => {
  const orderedQuestions = [...(input.questions || [])].sort(byOrder);

  const childrenBySubGroup = new Map<string, LayoutQuestion[]>();
  const topLevel: LayoutQuestion[] = [];
  for (const q of orderedQuestions) {
    if (q.subGroupId && q.subGroupRole === 'child') {
      if (!childrenBySubGroup.has(q.subGroupId)) childrenBySubGroup.set(q.subGroupId, []);
      childrenBySubGroup.get(q.subGroupId)!.push(q);
    } else {
      topLevel.push(q);
    }
  }

  let counter = input.startCounter ?? 0;
  const next = () => (counter += 1);
  const flat = groupChoicesInRun(topLevel, next);

  const items = flat.map((item): RenderItem => {
    if (item.kind !== 'question') return item;
    const q = item.question;
    if (q.subGroupId && q.subGroupRole === 'parent') {
      const kids = childrenBySubGroup.get(q.subGroupId) || [];
      return {
        kind: 'parent-group',
        subGroupId: q.subGroupId,
        parent: item,
        children: kids.map((child, idx) => ({ question: child, subPartIndex: idx + 1 })),
      };
    }
    return item;
  });

  return { items, endCounter: counter };
};

export const itemEffectiveMarks = (item: RenderItem): number => {
  if (item.kind === 'question') return item.question.marks || 0;
  if (item.kind === 'choice-group') return item.members[0]?.question.marks || 0;
  return item.children.reduce((sum, c) => sum + (c.question.marks || 0), 0);
};

export const sectionEffectiveMarks = (
  items: RenderItem[],
  selectionRule?: { attemptCount: number; totalCount: number } | null,
): number => {
  const contributions = items.map(itemEffectiveMarks);
  if (!selectionRule || !selectionRule.attemptCount) {
    return contributions.reduce((a, b) => a + b, 0);
  }
  const n = Math.min(selectionRule.attemptCount, contributions.length);
  // With uniform marks (enforced at publish time), any N contributions
  // yield the same total — take the first N for a deterministic value.
  return contributions.slice(0, n).reduce((a, b) => a + b, 0);
};

/**
 * True when every item in the section contributes the same effective marks —
 * required precondition for a `selectionRule` (attempt N of M) to produce a
 * deterministic total. Items with 0 effective marks (empty section) are
 * treated as uniform.
 */
export const sectionMarksAreUniform = (items: RenderItem[]): boolean => {
  if (items.length <= 1) return true;
  const first = itemEffectiveMarks(items[0]);
  for (let i = 1; i < items.length; i += 1) {
    if (itemEffectiveMarks(items[i]) !== first) return false;
  }
  return true;
};
